import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { VictoryPie, VictoryLegend, VictoryContainer, VictoryLabel } from 'victory';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Tooltip from './Tooltip';
import calculateStatisticsPercents from './helpers/calculateStatisticsPercents';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  donutTitle: {
    textTransform: 'uppercase',
    fontWeight: 500,
    color: theme.palette.text.grey,
    fontFamily: theme.typography.fontFamily,
    margin: '0 0 10px',
  },
}));

const LEGEND_LINE_MAX_LENGTH = 26;
const PERCENT_LABEL_OFFSET = 24;
const VALUE_LABEL_OFFSET = 20;
const DONUT_PADDING = 48;

const wrapLegendText = (text, maxLineLength = LEGEND_LINE_MAX_LENGTH) => {
  if (!text || typeof text !== 'string' || text.length <= maxLineLength) {
    return text;
  }

  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let currentLine = '';

  words.forEach(word => {
    if (word.length > maxLineLength) {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = '';
      }

      for (let index = 0; index < word.length; index += maxLineLength) {
        lines.push(word.slice(index, index + maxLineLength));
      }
      return;
    }

    const nextLine = currentLine ? `${currentLine} ${word}` : word;
    if (nextLine.length > maxLineLength) {
      lines.push(currentLine);
      currentLine = word;
      return;
    }

    currentLine = nextLine;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

const transformDonutData = (data, shouldPercent) => {
  const legendData = data
    .map(({ legendTitle, fillColor, hideLabel, withExtInfo, filterValue }) => {
      return {
        name: wrapLegendText(legendTitle),
        symbol: { fill: fillColor },
        hideLabel,
        labels: { fill: '#000000' },
        withExtInfo,
        dataKey: filterValue || legendTitle,
        isClickable: Boolean(filterValue),
      };
    })
    .filter(title => !title.hideLabel);
  const donutData = data
    .map(({ value, fillColor, hiddenInData, filterValue, legendTitle }) => {
      return {
        y: value,
        label: shouldPercent ? `${calculateStatisticsPercents(data, value)}%` : value,
        fillColor,
        hiddenInData,
        dataKey: filterValue || legendTitle,
        isClickable: Boolean(filterValue),
      };
    })
    .filter(item => !item.hiddenInData);

  return [legendData, donutData];
};

const Donut = props => {
  const classes = useStyles();
  const theme = useTheme();
  const [tooltipObj, setTooltipObj] = useState({
    x: 0,
    y: 0,
    show: false,
  });
  const {
    title,
    legendWidth,
    legendHeight,
    legendOrientation,
    legendItemsPerRow,
    donutWidth,
    donutHeight,
    donutEndAngle,
    classes: customClasses,
    donutData,
    donutRadius,
    onLegendClick,
    onDatumClick,
    shouldPercent,
    donutTitle,
    donutLegendTitle,
    donutLegendTitleStyles,
  } = props;
  const [legendData, transformedDonutData] = transformDonutData(donutData, shouldPercent);

  const legendChartProps = useMemo(
    () => ({
      data: legendData,
      orientation: legendOrientation,
      itemsPerRow: legendItemsPerRow,
      gutter: 5,
      title,
      style: {
        title: {
          textTransform: 'uppercase',
          fontWeight: 500,
          fill: theme.palette.text.grey,
          fontFamily: theme.typography.fontFamily,
        },
        labels: {
          fontFamily: theme.typography.fontFamily,
          fontSize: 14,
          lineHeight: 1.3,
        },
        data: {},
      },
    }),
    [legendData, legendOrientation, legendItemsPerRow, title, theme],
  );

  const legendDimensions = useMemo(() => VictoryLegend.getDimensions(legendChartProps), [legendChartProps]);

  const legendContainerWidth = Math.max(legendWidth, legendDimensions.width);
  const legendContainerHeight = Math.max(legendHeight, legendDimensions.height);

  const donutClasses = {
    root: classNames({
      [classes.root]: true,
      [customClasses.root]: !!customClasses.root,
    }),
    legendWrapper: classNames({
      [customClasses.legendWrapper]: !!customClasses.legendWrapper,
    }),
    donutWrapper: classNames({
      [customClasses.donutWrapper]: !!customClasses.donutWrapper,
    }),
  };

  return (
    <div className={donutClasses.root}>
      <div className={donutClasses.legendWrapper}>
        {donutLegendTitle && <p className={`${classes.donutTitle} ${donutLegendTitleStyles}`}>{donutLegendTitle}</p>}
        <VictoryContainer height={legendContainerHeight} width={legendContainerWidth}>
          <VictoryLegend
            {...legendChartProps}
            standalone={false}
            style={{
              ...legendChartProps.style,
              labels: {
                ...legendChartProps.style.labels,
                cursor: ({ datum }) => (datum.isClickable ? 'pointer' : 'default'),
              },
              data: {
                cursor: ({ datum }) => (datum.isClickable ? 'pointer' : 'default'),
              },
            }}
            labelComponent={<VictoryLabel lineHeight={1.3} />}
            events={[
              {
                target: 'data',
                eventHandlers: {
                  onClick: event => {
                    return [
                      {
                        target: 'data',
                        mutation: ({ datum }) => {
                          if (datum.withExtInfo) {
                            setTooltipObj({
                              x: event.clientX,
                              y: event.clientY,
                              show: true,
                            });
                            onLegendClick(props, donutData);
                          }
                          if (onDatumClick && datum.isClickable) {
                            onDatumClick(datum.dataKey);
                          }
                          return null;
                        },
                      },
                    ];
                  },
                },
              },
              {
                target: 'labels',
                eventHandlers: {
                  onClick: () => {
                    return [
                      {
                        target: 'labels',
                        mutation: ({ datum }) => {
                          if (onDatumClick && datum.isClickable) {
                            onDatumClick(datum.dataKey);
                          }
                          return null;
                        },
                      },
                    ];
                  },
                },
              },
            ]}
          />
        </VictoryContainer>
      </div>
      <div className={donutClasses.donutWrapper}>
        {donutTitle && <p className={classes.donutTitle}>{donutTitle}</p>}
        <VictoryContainer height={donutHeight} width={donutWidth}>
          <VictoryPie
            standalone={false}
            endAngle={donutEndAngle}
            data={transformedDonutData}
            padding={DONUT_PADDING}
            labelRadius={({ radius }) => radius + (shouldPercent ? PERCENT_LABEL_OFFSET : VALUE_LABEL_OFFSET)}
            style={{
              data: {
                fill: ({ datum }) => datum.fillColor,
                cursor: ({ datum }) => (datum.isClickable ? 'pointer' : 'default'),
              },
              labels: {
                fill: '#000000',
                fontSize: '14px',
                fontWeight: 'bold',
              },
            }}
            innerRadius={({ radius }) => {
              // Double ring thickness by expanding inward only (outer radius unchanged).
              const thickness = Math.max(0, radius - donutRadius);
              return Math.max(0, radius - thickness * 2);
            }}
            events={[
              {
                target: 'data',
                eventHandlers: {
                  onClick: () => {
                    return [
                      {
                        target: 'data',
                        mutation: ({ datum }) => {
                          if (onDatumClick && datum.isClickable) {
                            onDatumClick(datum.dataKey);
                          }
                          return null;
                        },
                      },
                    ];
                  },
                  onMouseOver: () => [
                    {
                      target: 'data',
                      mutation: ({ datum }) => ({
                        style: datum.isClickable
                          ? {
                              fill: datum.fillColor,
                              filter: 'drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.18))',
                              cursor: 'pointer',
                            }
                          : {
                              fill: datum.fillColor,
                              cursor: 'default',
                            },
                      }),
                    },
                  ],
                  onMouseOut: () => [
                    {
                      target: 'data',
                      mutation: () => null,
                    },
                  ],
                },
              },
            ]}
          />
        </VictoryContainer>
      </div>
      <Tooltip
        isOpen={tooltipObj.show}
        x={tooltipObj.x}
        y={tooltipObj.y}
        closeTooltip={(x, y) => {
          setTooltipObj({
            x,
            y,
            show: false,
          });
        }}
        donutData={donutData}
        shouldPercent={shouldPercent}
      />
    </div>
  );
};

Donut.defaultProps = {
  donutLegendTitleStyles: undefined,
  title: undefined,
  donutTitle: undefined,
  donutLegendTitle: undefined,
  classes: {},
  legendWidth: 400,
  legendHeight: 90,
  donutWidth: 400,
  donutHeight: 400,
  legendItemsPerRow: 2,
  legendOrientation: 'horizontal',
  donutEndAngle: -360,
  donutRadius: 120,
  donutData: [],
  onLegendClick: () => {},
  onDatumClick: undefined,
  shouldPercent: true,
};

Donut.propTypes = {
  donutLegendTitleStyles: PropTypes.string,
  title: PropTypes.string,
  donutTitle: PropTypes.string,
  donutLegendTitle: PropTypes.string,
  classes: PropTypes.shape({
    root: PropTypes.string,
    legendWrapper: PropTypes.string,
    donutWrapper: PropTypes.string,
    donutTitle: PropTypes.string,
  }),
  legendWidth: PropTypes.number,
  legendHeight: PropTypes.number,
  donutWidth: PropTypes.number,
  donutHeight: PropTypes.number,
  legendItemsPerRow: PropTypes.number,
  legendOrientation: PropTypes.oneOf(['horizontal', 'vertical']),
  donutEndAngle: PropTypes.number,
  donutData: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
      legendTitle: PropTypes.string,
      filterValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      fillColor: PropTypes.string,
      hideLabel: PropTypes.bool,
      withExtInfo: PropTypes.bool,
    }),
  ),
  donutRadius: PropTypes.number,
  onLegendClick: PropTypes.func,
  onDatumClick: PropTypes.func,
  shouldPercent: PropTypes.bool,
};

export default Donut;
